import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  CardActionArea,
  Box,
  Modal,
  IconButton,
} from '@mui/material';
import { includes } from 'lodash';
import React, { useRef, useState } from 'react';
import { irregularitiesSlice, useDispatch, useSelector } from '../../stores';
import { palette } from '../../theme';
import { handleDateTime } from '../../utils';

export const IrregularityCard = (props: {
  item: {
    id: number;
    licensePlate: string;
    vehicleBrand: string | null;
    vehicleCategory: string;
    observation: string | null;
    photoPlate: string;
    registrationDateTime: string;
  };
}) => {
  const dispatch = useDispatch();
  const cardRef = useRef(null);
  const [lightbox, setLightbox] = useState<{
    open: boolean;
    src: string;
    alt: string;
  }>({ open: false, src: '', alt: '' });
  const {
    irregularityList,
    irregularityOldList,
    irregularityNewList,
    selectedIds,
  } = useSelector((state) => state.irregularitiesReducer);

  const selected = includes(selectedIds, props.item.id);

  const openLightbox = (e: React.MouseEvent, src: string, alt: string) => {
    e.stopPropagation();
    setLightbox({ open: true, src, alt });
  };

  const closeLightbox = () =>
    setLightbox((prev) => ({ ...prev, open: false }));

  const handleClick = (irregularityId: number) => {
    let newSelectedIds: number[];

    if (!selected) {
      newSelectedIds = [...selectedIds, irregularityId];
    } else {
      newSelectedIds = selectedIds.filter((id) => id !== irregularityId);
    }

    dispatch(irregularitiesSlice.actions.setSelectedIds(newSelectedIds));

    const allSelected = irregularityList.every((item) =>
      newSelectedIds.includes(item.id),
    );
    dispatch(irregularitiesSlice.actions.setAllIdsSelected(allSelected));

    const allOldSelected = irregularityOldList.every((item) =>
      newSelectedIds.includes(item.id),
    );
    dispatch(irregularitiesSlice.actions.setAllOldIdsSelected(allOldSelected));

    const allNewSelected = irregularityNewList.every((item) =>
      newSelectedIds.includes(item.id),
    );
    dispatch(irregularitiesSlice.actions.setAllNewIdsSelected(allNewSelected));
  };

  const textBlockSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 0.15,
    padding: '12px',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    overflow: 'hidden',
  };

  const wrapSx = {
    width: '100%',
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'break-word' as const,
  };

  return (
    <Grid
      item
      xs={12}
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {selected && (
        <div
          style={{
            position: 'absolute',
            top: '-6px',
            right: '-10px',
            height: '25px',
            width: '25px',
            backgroundColor: palette.secondary.main,
            borderRadius: '100px',
            zIndex: 1,
          }}
        >
          <CheckIcon sx={{ fontSize: '25px', color: 'white' }} />
        </div>
      )}
      <Card
        sx={{
          width: '100%',
          borderRadius: '14px',
          border:
            selected
              ? `3px solid ${palette.secondary.main}`
              : `3px solid ${palette.primary.main}`,
        }}
        ref={cardRef}
        onClick={() => handleClick(props.item.id)}
      >
        <CardActionArea
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            height: {
              xs: 'auto',
              sm: 150,
            },
            minHeight: { xs: 170, sm: 150 },
          }}
        >
          <CardContent
            sx={{
              ...textBlockSx,
              display: 'flex',
              width: {
                xs: '100%',
                sm: '50%',
              },
            }}
          >
            <Typography
              variant='subtitle1'
              color='primary'
              sx={{ fontWeight: 700, lineHeight: 1.15 }}
            >
              #{props.item.id}
            </Typography>
            <Typography
              color='text.secondary'
              sx={{
                fontSize: '12px',
                lineHeight: 1.15,
                ...wrapSx,
              }}
            >
              {handleDateTime(props.item.registrationDateTime)}
            </Typography>
            <Typography
              color='text.secondary'
              sx={{
                fontSize: '13px',
                lineHeight: 1.25,
                ...wrapSx,
              }}
            >
              <strong>Placa:</strong> {props.item.licensePlate}
            </Typography>
            <Typography
              color='text.secondary'
              sx={{
                fontSize: '13px',
                lineHeight: 1.25,
                ...wrapSx,
              }}
            >
              <strong>Categoria:</strong> {props.item.vehicleCategory}
            </Typography>
            {props.item.vehicleBrand ? (
              <Typography
                color='text.secondary'
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.25,
                  ...wrapSx,
                }}
              >
                <strong>Modelo:</strong> {props.item.vehicleBrand}
              </Typography>
            ) : null}
            {props.item.observation ? (
              <Typography
                color='text.secondary'
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  ...wrapSx,
                }}
              >
                <strong>Observação:</strong> {props.item.observation}
              </Typography>
            ) : null}
          </CardContent>

          <Box
            sx={{
              width: {
                xs: '100%',
                sm: '50%',
              },
              height: { xs: 100, sm: '100%' },
              flex: {
                xs: 'none',
                sm: 1,
              },
              padding: '6px',
              boxSizing: 'border-box',
              minWidth: 0,
              overflow: 'hidden',
              backgroundColor: palette.grey[100],
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                flex: 1,
                alignSelf: 'stretch',
                minWidth: 0,
                minHeight: 0,
                width: '100%',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                cursor: props.item.photoPlate ? 'pointer' : 'default',
                backgroundColor: 'white',
              }}
              onClick={
                props.item.photoPlate
                  ? (e) =>
                      openLightbox(e, props.item.photoPlate, 'Foto da placa')
                  : undefined
              }
            >
              {props.item.photoPlate ? (
                <>
                  <img
                    src={props.item.photoPlate}
                    alt=''
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 30,
                      height: 30,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.45)',
                      color: 'white',
                      opacity: 0.9,
                      transition: 'opacity 120ms ease, transform 120ms ease',
                      pointerEvents: 'none',
                      transform: 'scale(1)',
                      '.MuiCardActionArea-root:hover &': {
                        opacity: 1,
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <ZoomInIcon sx={{ fontSize: 18 }} />
                  </Box>
                </>
              ) : (
                <Typography variant='caption' color='text.secondary'>
                  —
                </Typography>
              )}
            </Box>
          </Box>
        </CardActionArea>
      </Card>

      <Modal
        open={lightbox.open}
        onClose={closeLightbox}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            outline: 'none',
            position: 'relative',
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 24,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={closeLightbox}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
            }}
            size='small'
          >
            <CloseIcon />
          </IconButton>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      </Modal>
    </Grid>
  );
};
